<template lang="pug">
.search-filters-item(v-if="showFilter")
  input.search-filters-item__input(
    v-model="model",
    type="checkbox",
    :id="filter.id"
    :disabled="disabled"
  )
  label.search-filters-item__label(:for="filter.id")
    span {{ label }}
  button.search-filters-item__expand(
    v-if="hasChildren",
    type="button",
    :class="{ active }",
    @click.stop.prevent="active = !active",
    @keydown.enter.stop.prevent="active = !active"
  )
  TransitionExpand(v-if="hasChildren")
    .search-filters-item__filters(v-if="active", :key="`${label}-subFilters`")
      .search-filters-item__filters-inner
        BlueFilterItem(
          v-for="item in filter.filters",
          :key="'item'+item.id",
          :filter="item",
          :setFilterValue="setFilterValue"
          :executeSearch="executeSearch"
          :disable="disableChildren"
        )
</template>

<script>
import TransitionExpand from "../../transitions/Expand.vue";
export default {
  name: "BlueFilterItem",
  components: { TransitionExpand },
  props: ["filter", "setFilterValue", 'disable', 'executeSearch'],
  data() {
    return {
      active: false,
    };
  },
  computed: {
    disableChildren() {
      return !!(this.config.filterParentsExcludeChildren && ((this.hasChildren && this.model) || (this.disable)));
    },
    disabled() {
      return this.disable || false;
    },
    hasChildren() {
      return !!(this.filter.filters && this.filter.filters.length);
    },
    label() {
      if (this.config.showFiltersCount) {
        return `${this.filter.value} (${this.filter.count})`;
      }
      return this.filter.value;
    },
    reloading() {
      return this.$store.getters.reloading;
    },
    showFilter() {
      if (this.config.showZeroResultsFilters) {
        return this.filter.count >= 0 || this.model
      } else {
        return this.filter.count > 0 || this.model
      }
    },
    model: {
      get() {
        return this.filter.selected;
      },
      set(value) {
        this.setFilterValue(this.filter, value);

        if (this.config.filterParentsExcludeChildren && value) {
          if (this.hasChildren) {
            const recurse = (filter) => {
              this.setFilterValue(filter, false);

              if (filter.filters && filter.filters.length) {
                filter.filters.forEach(recurse);
              }
            }

            this.filter.filters.forEach(recurse);
          }
        }
      },
    },
  },
  methods: {
    parseTreeActiveState(filters) {
      let hasSelections = false;
      filters.forEach((filter) => {
        if (filter.selected) hasSelections = true;
        else if (filter.filters && filter.filters.length) {
          if (this.parseTreeActiveState(filter.filters)) hasSelections = true;
        }
      });
      return hasSelections;
    },
  },
  watch: {
    reloading() {
      if (this.hasChildren) {
        this.active = this.parseTreeActiveState(this.filter.filters);
      }
    },
  },
  created() {
    if (this.hasChildren) {
      if (this.parseTreeActiveState(this.filter.filters)) {
        this.active = true;
      }
    }
  },
};
</script>

<style lang="scss" scoped>
.search-filters-item {
  display: grid;
  grid-template-columns: 1fr auto;
  grid-template-rows: auto auto;
  position: relative;
  flex: 0 0 auto;
  font-size: 1.125rem;
  padding-top: 0.5rem;
  &:not(:last-child) {
    padding-bottom: 0.5rem;
    border-bottom: 1px solid transparent;
    &::after {
      content: '';
      position: absolute;
      bottom: 0;
      left: 0;
      right: 0;
      height: 1px;
      background-color: var(--border-color);
      opacity: 0.5;
    }
  }

  &__input {
    @include hideVisually;
    &:focus + .search-filters-item__label {
      &::before {
        box-shadow: inset 0 0 0 2px var(--check-active-border);
      }
    }
    &:checked + .search-filters-item__label {
      &::before {
        background-color: var(--check-active-background);
        border-color: var(--check-active-border);
      }
      &::after {
        opacity: 1;
      }
    }

    &:disabled + .search-filters-item__label {
      opacity: 0.5;
      cursor: default;
    }

    &:disabled + .search-filters-item__expand {
      opacity: 0.5;
    }
  }

  &__label {
    grid-area: 1 / 1 / 2 / 2;
    display: flex;
    cursor: pointer;
    align-items: flex-start;
    text-align: left;
    font-size: 0.875rem;
    line-height: 1.5;
    position: relative;

    // .search-filters-item__filters &:last-child {
    //   margin-left: 1.5rem;
    // }

    &::before {
      content: "";
      display: flex;
      justify-content: center;
      align-items: center;
      height: 1.25rem;
      width: 1.25rem;
      flex: 0 0 1.25rem;
      margin-right: 0.75rem;
      font-size: 1rem;
      border: 1px solid var(--check-border);
      transition: 0.25s ease;
    }

    &::after {
      content: "";
      height: 0.75rem;
      width: 0.3875rem;
      color: var(--check-active-color);
      border-bottom: 0.125rem solid currentColor;
      border-right: 0.125rem solid currentColor;
      position: absolute;
      left: 0.45rem;
      top: 0.125rem;
      transform: rotate(45deg);
      opacity: 0;
      transition: 0.25s ease;
    }
  }

  &__expand {
    all: unset;
    appearance: none;
    background: none;
    grid-area: 1 / 2 / 2 / 3;
    cursor: pointer;
    background-color: currentColor;
    margin-left: 0.5rem;
    width: 1.25rem;
    height: 1.25rem;
    position: relative;
    opacity: 0.2;
    transition: 0.2s ease;
    &::before,
    &::after {
      content: "";
      position: absolute;
      top: 50%;
      left: 50%;
      margin-left: -0.3125rem;
      margin-top: -0.0625rem;
      width: 0.625rem;
      height: 0.125rem;
      background-color: currentColor;
      transition: transform 0.2s ease;
      filter: brightness(0) invert(1);
    }
    &::after {
      transform: rotate(90deg);
    }
    &:hover {
      color: var(--accordion-title-active-color);
      opacity: 0.6;
    }
    &.active {
      &::after {
        transform: rotate(0deg);
      }
    }
    &.active,
    &:hover {
      opacity: 0.6;
      color: var(--accordion-title-active-color);
    }
  }

  &__filters {
    grid-area: 2 / 1 / 3 / 3;

    &-inner {
      display: flex;
      flex-direction: column;
      margin-top: 0.75rem;
      margin-left: 0.875rem;
    }
  }
}
</style>